import { memo } from 'react';
import styled from '@emotion/styled';
import { Typography } from '@mui/material';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { TitleEntity } from '@/redux/slices/slice_types';
import { setActiveTitle, setActiveTitleLevel, setSelectedTitleId, updateActiveTitleHierarchy } from '@/redux/slices/titleSlice';
import { selectPackagingActiveTemplate } from '@/redux/selectors/packagingSelectors';
import { selectActiveTitleLevel, selectSelectedTitleId } from '@/redux/selectors/titleSelectors';
import TitleBrowserEntity from '@/components/OrderPlacement/OPStep2FileSelection/TitleBrowser/TitleBrowserEntity';
import TitleBrowserAccordion from '@/components/OrderPlacement/OPStep2FileSelection/TitleBrowser/TitleBrowserAccordion';
import TitleBrowserItemMenu from '@/components/OrderPlacement/OPStep2FileSelection/TitleBrowser/TitleBrowserItemMenu/TitleBrowserItemMenu';

type TitleBrowserItemProps = {
  data: TitleEntity;
  level: number;
};

const TitleBrowserItem = ({ data, level }: TitleBrowserItemProps) => {
  const { id, name, entity_type, children } = data;
  const dispatch = useAppDispatch();
  const packagingActiveTemplate = useAppSelector(selectPackagingActiveTemplate);
  const activeTitleLevel = useAppSelector(selectActiveTitleLevel);
  const selectedTitleId = useAppSelector(selectSelectedTitleId);
  const isLastChild = !children?.length;
  const hasVersions = children?.find((c: TitleEntity) => c.entity_type === 'version') || isLastChild;

  if (isLastChild || hasVersions) {
    return <TitleBrowserEntity entity={data} />;
  }

  const handleAccordionExpand = () => {
    dispatch(setSelectedTitleId(id));
    dispatch(setActiveTitleLevel(level));
    dispatch(updateActiveTitleHierarchy({ id, level }));

    if (level >= 3) {
      dispatch(setActiveTitle(id));
    }
  };

  const childType = children?.[0]?.entity_type;

  return (
    <TitleBrowserAccordion
      defaultExpanded={id === selectedTitleId && activeTitleLevel === level}
      expandedCallback={handleAccordionExpand}
      title={
        <TitleRow>
          <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Typography sx={{ ml: 1 }}>{name}</Typography>
            {!isLastChild && !hasVersions && (
              <div style={{ display: 'flex', flexGrow: 1, justifyContent: 'space-between', marginLeft: 'auto' }}>
                <div style={{ display: 'flex', alignItems: 'center', opacity: 0.5 }}>
                  <span style={{ margin: '0 10px' }}>|</span>
                  <Typography>
                    {children?.length} {entity_type === 'marketing' ? 'Marketing Item' : childType}
                    {children?.length !== 1 ? 's' : ''}
                  </Typography>
                </div>
                {
                  // If `packagingActiveTemplate` is not null, this mean this component is rendered on the Packaging page,
                  // and in that case we don't want to display "Add all" dropdown (`TitleBrowserItemMenu` component)
                  !packagingActiveTemplate && (
                    <>
                      <div style={{ flexGrow: 1 }} />
                      <TitleBrowserItemMenu parentId={id} items={children} />
                    </>
                  )
                }
              </div>
            )}
          </div>
        </TitleRow>
      }
    >
      {children?.map((child: TitleEntity) => <TitleBrowserItem key={child.id} level={level + 1} data={child} />)}
    </TitleBrowserAccordion>
  );
};

export default memo(TitleBrowserItem);

const TitleRow = styled.div(({ theme }) => ({
  justifyContent: 'flex-start',
  width: '100%',
  padding: theme.spacing(1.5, 2, 1.5, 0),
  color: theme.palette.text.primary,
  textTransform: 'none',
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  flexDirection: 'row',
  marginRight: 10,

  '.MuiTypography-root, span': {
    fontSize: '1.2rem',
    whiteSpace: 'nowrap'
  }
}));
